/**
 * Display-role model (SCRUM-387, v7 role-based UI).
 *
 * SINGLE SOURCE for the stored→display role mapping. The backend stores
 * `SystemRole` enum values (Executive / BankOfficer / BranchEmployee /
 * BranchManager — see BankIQ.Core.Domain.Users.SystemRole); the UI renders the
 * v7 display roles. Mapping at this boundary avoids migrating stored enum
 * values, which feed EF conversions and the SCRUM-102 StoredRoleResolver
 * (plan decision D2-revised, roles-rbac-ui-v7-plan.md).
 *
 * `Platform Admin` and `Unassigned` are CLIENT-ONLY roles/states with no
 * stored counterpart until SCRUM-103 delivers real RBAC.
 */
export type DisplayRole =
  | 'Bank Executive'
  | 'Bank Officer'
  | 'Branch Manager'
  | 'Teller'
  | 'Bank Admin'
  | 'Platform Admin'
  | 'Unassigned';

/**
 * EVERY value of the server's `SystemRole` enum
 * (`BankIQ.Core.Domain.Users.SystemRole`), which is the authoritative list.
 *
 * SCRUM-913 — WHY THIS UNION EXISTS. `STORED_TO_DISPLAY` was previously typed
 * `Record<string, DisplayRole>`. A `string` index signature accepts ANY key, so
 * the compiler could not see that a server role was missing from the map: an
 * unmapped role fell through `toDisplayRole` as its raw stored value, matched no
 * `NAV_ITEMS.roles` entry, and rendered an EMPTY sidebar. That defect shipped
 * three times — SCRUM-573 (Executive), SCRUM-583, SCRUM-913 (BankAdmin) — and
 * each fix only added the one missing key, leaving the trap armed.
 *
 * Keying the map on this union makes the omission a COMPILE error instead: add a
 * seventh value to the server enum, extend this union, and `tsc` enumerates
 * every place that must be updated. Verified by mutation — deleting a key from
 * `STORED_TO_DISPLAY` fails the build (SCRUM-945 acceptance evidence).
 */
export type StoredRole =
  | 'Executive'
  | 'BankOfficer'
  | 'BranchManager'
  | 'BranchEmployee'
  | 'BankAdmin'
  | 'PlatformAdmin';

/**
 * Stored SystemRole value → v7 display role. EXHAUSTIVE by construction (see
 * {@link StoredRole}). `Bank Admin` matches the label the server already renders
 * for this role (`SettingsUserService.MapRole`), so the two agree.
 */
export const STORED_TO_DISPLAY: Readonly<Record<StoredRole, DisplayRole>> = {
  Executive: 'Bank Executive',
  BankOfficer: 'Bank Officer',
  BranchManager: 'Branch Manager',
  BranchEmployee: 'Teller',
  BankAdmin: 'Bank Admin',
  PlatformAdmin: 'Platform Admin',
};

/**
 * Lookup view of {@link STORED_TO_DISPLAY} for UNTRUSTED keys.
 *
 * The const itself is typed `Record<StoredRole, DisplayRole>` to buy compile-time
 * exhaustiveness (SCRUM-913). But at the two boundaries below, the key is an
 * arbitrary string from the server or the IdP, so the lookup genuinely CAN miss.
 * Casting the key to `StoredRole` instead would lie to the compiler: it would
 * type the result as always-present and make the `??` / `undefined` fallbacks
 * look like dead code (`@typescript-eslint/no-unnecessary-condition` flags them),
 * inviting a future "cleanup" to delete the very fallbacks that keep
 * `toDisplayRole` idempotent. This view keeps the type honest instead.
 */
const STORED_LOOKUP: Readonly<Record<string, DisplayRole | undefined>> = STORED_TO_DISPLAY;

/**
 * Maps a stored role value to its display LABEL; passes unknowns through.
 *
 * The pass-through is deliberate and load-bearing — do NOT change it to a
 * sentinel. This function is idempotent on values that are already display
 * roles (they are not keys of {@link STORED_TO_DISPLAY}), and both
 * {@link useDisplayRole} (fed `user.role`, already a display role) and the admin
 * surfaces (fed arbitrary server `grantedRole` strings) rely on that. A sentinel
 * fallback here would render EVERY role badge in the app as the sentinel, and
 * would mislabel an unfamiliar server role instead of showing what the server
 * said. Fail-safe GATING lives in {@link toSessionRole} instead (SCRUM-913).
 */
export function toDisplayRole(stored: string): string {
  return STORED_LOOKUP[stored] ?? stored;
}

/**
 * Reverse of {@link STORED_TO_DISPLAY} for the four bank-assignable roles only
 * (SCRUM-465). Platform Admin / Unassigned are deliberately absent — they are
 * Intersect-managed / transient and never aliasable. Used to resolve a display
 * role back to its stored key when overlaying a tenant alias.
 */
export const DISPLAY_TO_STORED: Readonly<Record<string, string>> = {
  'Bank Executive': 'Executive',
  'Bank Officer': 'BankOfficer',
  'Branch Manager': 'BranchManager',
  Teller: 'BranchEmployee',
};

/**
 * Overlays a tenant's per-role display alias (SCRUM-465) on a display role.
 * PURE (no hook/store) so it is trivially unit-testable; the {@link
 * useDisplayRole} hook supplies `aliases` from the shared react-query cache.
 *
 * Falls back to the built-in `displayRole` when: the role isn't a bank
 * (aliasable) role, there is no alias set, or the alias is blank/whitespace —
 * so aliasing NEVER yields an empty label or the raw enum value (SC7). Platform
 * Admin / Unassigned pass through unchanged (SC8).
 */
export function applyRoleAlias(
  displayRole: string,
  aliases: Readonly<Record<string, string>> | undefined,
): string {
  if (!aliases) return displayRole;
  const stored = DISPLAY_TO_STORED[displayRole];
  if (stored === undefined) return displayRole; // not an aliasable bank role
  const alias = aliases[stored];
  return alias !== undefined && alias.trim().length > 0 ? alias : displayRole;
}

/** The switcher's SWITCH ROLE section, in v7 dropdown order. */
export const SWITCHABLE_ROLES: readonly DisplayRole[] = [
  'Bank Executive',
  'Bank Officer',
  'Branch Manager',
  'Teller',
  // The two ADMIN identities close the list, after the four bank actors.
  // Bank Admin is granted via the `bankiq-bank-admins` IdP group (SCRUM-913),
  // never assigned in-app — hence absent from BANK_ASSIGNABLE_ROLES below.
  'Bank Admin',
  'Platform Admin',
];

/**
 * Bank-side roles an admin can ASSIGN to a bank user (Settings › Roles &
 * permissions, admin Team & Roles, Add-user). This is deliberately NARROWER
 * than {@link SWITCHABLE_ROLES}: `Platform Admin` is an Intersect-managed
 * identity (never assignable to a bank tenant's user) and `Unassigned` is a
 * transient account STATE, not an assignable role. Mirrors the prototype's
 * `rolesData` order (app-shell 7.html: exec, officer, branchmgr, employee).
 */
export type AssignableRole = 'Bank Executive' | 'Bank Officer' | 'Branch Manager' | 'Teller';

export const BANK_ASSIGNABLE_ROLES: readonly AssignableRole[] = [
  'Bank Executive',
  'Bank Officer',
  'Branch Manager',
  'Teller',
];

/**
 * Feature modules a role's permissions are granted against (v7 `RBAC_MODULES`).
 * Onboarding is intentionally excluded — it is a platform-side (Intersect)
 * surface, not a bank-tenant module.
 *
 * NOTE (SCRUM-31 / SCRUM-103 boundary): this list and {@link DEFAULT_PERMISSION_MATRIX}
 * are Settings CONFIG DATA describing the RBAC matrix spec (SCRUM-31). They do
 * NOT implement server-side enforcement — `ModuleAccessPolicy` is the sole
 * authority (SCRUM-103).
 *
 * SCRUM-914 DIVERGENCE — read before editing. These defaults NO LONGER mirror
 * enforcement. SCRUM-914 removed Vault from Bank Officer / Branch Manager and
 * Settings from Bank Executive on both tiers, but the defaults below still grant
 * them, so the Settings "Roles & permissions" grid can display a grant the
 * platform refuses with a 403. It is cosmetic (nothing here gates anything —
 * `hasModuleAccess` is currently unused), but realigning it needs a product call:
 * should a module the platform hard-gates be toggleable in the grid at all?
 * Tracked separately; do not silently "fix" one side of this pair — the frontend
 * default and the backend `DefaultPermissionMatrixJson` must stay identical.
 */
export const RBAC_MODULES = ['Campaigns', 'Vault', 'Ask BankIQ', 'Referrals', 'Settings'] as const;

export type RbacModule = (typeof RBAC_MODULES)[number];

/** A role's grant map across every {@link RBAC_MODULES} module. */
export type ModulePermissions = Readonly<Record<RbacModule, boolean>>;

/**
 * Default permission matrix for the four system bank roles, verbatim from the
 * prototype's `rolesData[*].permissions` (app-shell 7.html). This is the
 * seed/spec the Settings permissions matrix renders and persists against.
 */
export const DEFAULT_PERMISSION_MATRIX: Readonly<Record<AssignableRole, ModulePermissions>> = {
  'Bank Executive': {
    Campaigns: true,
    Vault: true,
    'Ask BankIQ': true,
    Referrals: true,
    Settings: true,
  },
  'Bank Officer': {
    Campaigns: true,
    Vault: true,
    'Ask BankIQ': false,
    Referrals: true,
    Settings: false,
  },
  'Branch Manager': {
    Campaigns: true,
    Vault: true,
    'Ask BankIQ': false,
    Referrals: true,
    Settings: false,
  },
  Teller: {
    Campaigns: false,
    Vault: false,
    'Ask BankIQ': false,
    Referrals: true,
    Settings: false,
  },
};

/**
 * Whether `role` is granted `module` under the DEFAULT matrix. Unknown roles
 * (e.g. custom roles or `Platform Admin`/`Unassigned`) return `false` — they
 * have no default grant. Callers persisting a custom matrix should consult
 * their own stored grants rather than this default.
 */
export function hasModuleAccess(role: string, module: RbacModule): boolean {
  const grants = (DEFAULT_PERMISSION_MATRIX as Record<string, ModulePermissions | undefined>)[role];
  return grants?.[module] ?? false;
}

export interface DemoIdentity {
  displayName: string;
  email: string;
  /**
   * Branch for branch-scoped roles (v7 getCurrentUser). 'West William Main'
   * is the largest REAL branch in the seeded dataset (406 of 2000 customers)
   * — the prototype's fictional 'Winchester Main' has no data here.
   */
  branch?: string;
}

/**
 * Demo identities per role — v7's getCurrentUser() bound to REAL seeded users
 * (bankiq_shared.users, SCRUM-308 roster) so referrals and teammate lookups
 * resolve. Platform Admin is an Intersect-side identity (not a bank user);
 * Unassigned keeps the signed-in identity (an authenticated user with no
 * admin-granted role).
 */
export const DEMO_IDENTITIES: Readonly<Partial<Record<DisplayRole, DemoIdentity>>> = {
  'Bank Executive': { displayName: 'Carol Whitfield', email: 'carol.whitfield@demo.bankiq' },
  'Bank Officer': { displayName: 'Priya Desai', email: 'priya.desai@demo.bankiq' },
  'Branch Manager': {
    displayName: 'Marcus Hale',
    email: 'marcus.hale@demo.bankiq',
    branch: 'West William Main',
  },
  Teller: {
    displayName: 'Lucia Vargas',
    email: 'lucia.vargas@demo.bankiq',
    branch: 'West William Main',
  },
  'Platform Admin': { displayName: 'Intersect Admin', email: 'admin@intersectgrp.net' },
};

/**
 * Landing nav id per role (v7 ROLE_LANDING). `null` = no landing — the
 * Unassigned pending-access state renders instead of any page.
 */
export const ROLE_LANDING: Readonly<Record<DisplayRole, string | null>> = {
  'Bank Executive': 'dashboard',
  'Bank Officer': 'dashboard',
  'Branch Manager': 'dashboard',
  Teller: 'dashboard',
  // Bank Admin's server grants equal Bank Executive's (ModuleAccessPolicy),
  // so it lands on the same page (SCRUM-913).
  'Bank Admin': 'dashboard',
  'Platform Admin': 'customer-dashboard',
  Unassigned: null,
};

/**
 * v7 canActOnReferrals: Exec/Officer/Branch Manager act on referrals
 * (scope toggle, approve/release, insights section, nav badge); Tellers
 * submit and track their own.
 */
export function canActOnReferrals(role: string): boolean {
  return role === 'Bank Executive' || role === 'Bank Officer' || role === 'Branch Manager';
}

/** Branch-scoped roles see branch-filtered dashboard data (v7). */
export function isBranchScoped(role: string): boolean {
  return role === 'Branch Manager' || role === 'Teller';
}

/** sessionStorage key for the demo-switched role (session-scoped by design — RT1). */
export const DEMO_ROLE_STORAGE_KEY = 'bankiq-demo-role';

const ALL_DISPLAY_ROLES: readonly DisplayRole[] = [...SWITCHABLE_ROLES, 'Unassigned'];

/**
 * Resolves the ACTING role for a session from an IdP role claim — an untrusted
 * string that TypeScript cannot constrain (SCRUM-913).
 *
 * Deliberately separate from {@link toDisplayRole}, because gating and labelling
 * have different failure requirements:
 *
 * - LABELLING wants to show whatever the server said, even an unfamiliar value.
 * - GATING must never let an unrecognised value become the acting role. Every
 *   nav/route check is `roles.includes(role)`, so an unmatched role yields an
 *   EMPTY shell with no explanation — indistinguishable, to the user, from a
 *   broken app (the SCRUM-573 / SCRUM-583 / SCRUM-913 symptom).
 *
 * So this fails LOUD (logs the offending claim, making a fourth occurrence
 * diagnosable rather than silent) and SAFE (`'Unassigned'` renders the existing
 * pending-access screen: "no role granted, contact your administrator").
 *
 * Matching is EXACT by design. A claim differing only in case (`"bankadmin"`)
 * degrades and logs rather than being coerced, because a casing divergence
 * between the IdP and `SystemRole` is a real provisioning defect that should
 * surface, not be silently absorbed.
 */
export function toSessionRole(claim: string): DisplayRole {
  const mapped = STORED_LOOKUP[claim];
  if (mapped !== undefined) return mapped;
  // Already a display role — the demo role switcher and any re-resolution path
  // pass these straight through.
  if (ALL_DISPLAY_ROLES.includes(claim as DisplayRole)) return claim as DisplayRole;
  console.error(
    `[auth] Unrecognised role claim "${claim}" — falling back to Unassigned. ` +
      `Add it to StoredRole/STORED_TO_DISPLAY in features/auth/roles.ts (SCRUM-913).`,
  );
  return 'Unassigned';
}

/** Reads the persisted demo role; null when absent/invalid/unavailable. */
export function readPersistedDemoRole(): DisplayRole | null {
  try {
    const raw = window.sessionStorage.getItem(DEMO_ROLE_STORAGE_KEY);
    return ALL_DISPLAY_ROLES.includes(raw as DisplayRole) ? (raw as DisplayRole) : null;
  } catch {
    return null;
  }
}

/** Persists the demo role; failures (privacy mode) are non-fatal. */
export function persistDemoRole(role: DisplayRole): void {
  try {
    window.sessionStorage.setItem(DEMO_ROLE_STORAGE_KEY, role);
  } catch {
    // sessionStorage unavailable — the switch still applies for this render tree.
  }
}
